import type { API } from '@editorjs/editorjs';
import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../Input/Input';

export interface YoutubeEmbedData {
  url: string;
}

export type YoutubeEmbedSaveDataFn = (url: string) => void;

interface YouTubeEmbedBodyProps {
  /**
   * Save url callback
   */
  saveData: YoutubeEmbedSaveDataFn;
  /**
   * Editor js api object
   */
  api: API;
  /**
   * Data with yt url
   */
  data?: YoutubeEmbedData;
  /**
   * If plugin is disabled
   */
  disabled?: boolean;
}

const YouTubeEmbedBody = ({
  saveData,
  data,
  api,
  disabled = false,
}: YouTubeEmbedBodyProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dataUrl, setDataUrl] = useState(data?.url || '');
  const [currentUrl, setCurrentUrl] = useState(data?.url || '');

  const videoId = useMemo(() => {
    const matches = dataUrl.match(
      /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/,
    );
    if (matches) return matches[1];
    return null;
  }, [dataUrl]);

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (event) => {
      setDataUrl(event.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.code === 'Enter') {
        inputRef.current?.blur();
      }
      if (event.code === 'Backspace' && currentUrl === '') {
        event.stopPropagation();
        event.preventDefault();
        api.blocks.delete(api.blocks.getCurrentBlockIndex());
      }
    },
    [api.blocks, currentUrl],
  );

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const newUrl = event.target.value;
      setCurrentUrl(newUrl);
      saveData(newUrl);
    },
    [saveData],
  );

  return (
    <div className="py-3">
      {videoId && (
        <div className="relative pb-[56.25%] mb-2">
          <iframe
            className="absolute w-full h-full"
            title={videoId}
            src={`https://www.youtube.com/embed/${videoId}`}
            allowFullScreen
          />
        </div>
      )}
      <Input
        refs={inputRef}
        value={currentUrl}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={t('ContentForm.YouTubePlaceholder')}
        error={!!dataUrl && !videoId ? t('ContentForm.Errors.YouTubeUrl') : ''}
        disabled={disabled}
        autoFocus
      />
    </div>
  );
};

export default YouTubeEmbedBody;
